import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  IconButton,
  Alert,
  Chip,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import Draggable from 'react-draggable';
import { Link } from 'react-router-dom';
import { Annotation, MacroImage, BaselineAnnotationContext } from '../types';

interface DermatoscopicPanelProps {
  apiBaseUrl: string;
  selectedAnnotation: Annotation | null;
  selectedMacroImage: MacroImage | null;
  uploadingMicroImage: boolean;
  microFileInputRef: React.RefObject<HTMLInputElement>;
  baselineContext?: BaselineAnnotationContext | null;
  isLoading?: boolean;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenCamera: () => void;
  onGoToMacroView: () => void;
  onClearBaselineContext?: () => void;
  onGoToBaselineView?: () => void;
}

const DermatoscopicPanel: React.FC<DermatoscopicPanelProps> = ({
  apiBaseUrl,
  selectedAnnotation,
  selectedMacroImage,
  uploadingMicroImage,
  microFileInputRef,
  baselineContext,
  isLoading = false,
  onImageUpload,
  onOpenCamera,
  onGoToMacroView,
  onClearBaselineContext,
  onGoToBaselineView
  
}) => {
  const theme = useTheme();
  const [showReferencePopup, setShowReferencePopup] = React.useState(false);
  const [cameraWithReference, setCameraWithReference] = React.useState(false);
  const [replaceMode, setReplaceMode] = React.useState(false);

  // Determine which annotation to display
  const displayAnnotation = baselineContext?.followupAnnotation || baselineContext?.baselineAnnotation || selectedAnnotation;
  const isBaselineMode = !!baselineContext;

  if (!displayAnnotation) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          {isLoading ? (
            <Box>
              <CircularProgress sx={{ mb: 2 }} />
              <Skeleton variant="text" width="60%" sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width="80%" sx={{ mx: 'auto', mb: 2 }} />
              <Skeleton variant="rectangular" width={120} height={36} sx={{ mx: 'auto' }} />
            </Box>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                No Annotation Selected
              </Typography>
              <Typography color="text.secondary" paragraph>
                {isBaselineMode 
                  ? "Please select a baseline annotation to add follow-up dermatoscopic images."
                  : "Please select an annotation from the macroscopic view to add dermatoscopic images."
                }
              </Typography>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={isBaselineMode && onGoToBaselineView ? onGoToBaselineView : onGoToMacroView}
                disabled={isLoading}
              >
                {isBaselineMode ? 'Go to Macroscopic View' : 'Go to Macroscopic View'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const handleCameraClick = () => {
    if (isBaselineMode && baselineContext?.baselineAnnotation?.linkedMicroId && baselineContext?.baselineAnnotation?.linkedMicroUrl) {
      setReplaceMode(false);
      setShowReferencePopup(true);
    } else {
      onOpenCamera();
    }
  };

  const handleReplaceFollowupClick = () => {
    if (baselineContext?.baselineAnnotation?.linkedMicroId && baselineContext?.baselineAnnotation?.linkedMicroUrl) {
      setReplaceMode(true);
      setShowReferencePopup(true);
    }
  };

  const handleCloseReferencePopup = () => {
    setShowReferencePopup(false);
    setCameraWithReference(false);
    setReplaceMode(false);
  };

  const handleProceedWithCamera = () => {
    setCameraWithReference(true);
    onOpenCamera();
  };

  const handleProceedWithUpload = () => {
    setShowReferencePopup(false);
    setReplaceMode(false);
    microFileInputRef.current?.click();
  };

  const PaperComponent = (props: any) => {
    return (
      <Draggable
        handle="#draggable-dialog-title"
        cancel={'[class*="MuiDialogContent-root"]'}
      >
        <Paper {...props} />
      </Draggable>
    );
  };

  return (
    <Box>
      {/* Context header */}
      {isBaselineMode && baselineContext && (
        <Card sx={{ mb: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <TimelineIcon color="info" />
                )}
                <Box>
                  {isLoading ? (
                    <>
                      <Skeleton variant="text" width={300} />
                      <Skeleton variant="text" width={250} />
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Follow-up Mode: Adding to Baseline Annotation
                        <Chip
                          size="small"
                          label="Baseline"
                          color="info"
                          variant="outlined"
                        />
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Baseline annotation: <strong>{baselineContext.baselineAnnotation.label}</strong>
                        {baselineContext.baselineAnnotation.linkedMicroId && (
                          <span> • Has existing dermatoscopic image</span>
                        )}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              {onClearBaselineContext && (
                <Button
                  size="small"
                  onClick={onClearBaselineContext}
                  startIcon={<ArrowBackIcon />}
                  disabled={isLoading}
                >
                  Exit Baseline Mode
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            {isLoading ? (
              <Skeleton variant="text" width={300} height={32} />
            ) : (
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoCameraIcon color="primary" />
                Dermatoscopic View: {displayAnnotation.label}
                {isBaselineMode && (
                  <Chip
                    size="small"
                    icon={<LinkIcon />}
                    label="Baseline Link"
                    color="info"
                    variant="filled"
                  />
                )}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isLoading ? (
                <>
                  <Skeleton variant="rectangular" width={120} height={32} />
                  <Skeleton variant="rectangular" width={100} height={32} />
                </>
              ) : (
                <>
                  {/* ABCDE Analysis Link */}
                  {displayAnnotation.id && (
                    <Button
                      component={Link}
                      to={`/abcde/${displayAnnotation.id}`}
                      variant="contained"
                      size="small"
                      startIcon={<AssessmentIcon />}
                      color="secondary"
                      sx={{ mr: 1 }}
                      disabled={isLoading}
                    >
                      ABCDE Analysis
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={onGoToMacroView}
                    disabled={isLoading}
                  >
                    {isBaselineMode ? 'Back to Baseline' : 'Back to Macro'}
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Loading state for image section */}
          {isLoading && (
            <Alert severity="info" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Loading dermatoscopic image data...
            </Alert>
          )}

          {/* Current dermatoscopic image */}
          {!isLoading && displayAnnotation.linkedMicroId && displayAnnotation.linkedMicroUrl ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {isBaselineMode ? (
                  baselineContext?.followupAnnotation ? "Follow-up Dermatoscopic Image" : "Baseline Dermatoscopic Image"
                ) : (
                  "Current Dermatoscopic Image"
                )}
                {isBaselineMode && !baselineContext?.followupAnnotation && (
                  <Chip
                    size="small"
                    label="From Baseline"
                    color="info"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
                {isBaselineMode && baselineContext?.followupAnnotation && (
                  <Chip
                    size="small"
                    label="New Follow-up"
                    color="success"
                    variant="filled"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  height: 400,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }
                }}
              >
                <img
                  src={`${apiBaseUrl}${displayAnnotation.linkedMicroUrl}`}
                  alt={`Dermatoscopic view of ${displayAnnotation.label}`}
                />
              </Box>
            </Box>
          ) : !isLoading ? (
            <Alert severity="error" sx={{ mb: 5}}>
              {isBaselineMode 
                ? "No dermatoscopic image exists for this baseline annotation. Add a follow-up image below."
                : "No dermatoscopic image linked to this annotation yet. Apply above 1% manual zoom and focus using the camera for autoSegmentation to work."
              }
            </Alert>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width={250} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" width="100%" height={400} />
            </Box>
          )}

          {/* Upload controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <input
              ref={microFileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={onImageUpload}
            />
            
            {isLoading ? (
              <>
                <Skeleton variant="rectangular" width={180} height={36} />
                <Skeleton variant="rectangular" width={140} height={36} />
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => microFileInputRef.current?.click()}
                  disabled={uploadingMicroImage || isLoading}
                >
                  {isBaselineMode 
                    ? (baselineContext?.followupAnnotation ? "Replace Follow-up Image" : "Add Follow-up Image")
                    : displayAnnotation.linkedMicroId 
                      ? "Replace Image" 
                      : "Upload Image"
                  }
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={handleCameraClick}
                  disabled={uploadingMicroImage || isLoading}
                  sx={{
                    // Add visual hint for zoom capability
                    '&:hover': {
                      bgcolor: 'action.hover',
                      '& .MuiButton-startIcon': {
                        transform: 'scale(1.1)'
                      }
                    }
                  }}
                >
                  {isBaselineMode
              ? (baselineContext?.followupAnnotation 
                  ? "Replace Follow-up Photo"
                  : "Capture Follow-up")
              : (displayAnnotation.linkedMicroId 
                  ? "Retake Photo" 
                  : "Take Photo")}
                  <Typography variant="caption" component="span" sx={{ ml: 0.5, fontSize: '0.6rem', opacity: 0.7 }}>
                    (Zoom Available)
                  </Typography>
                </Button>
              </>
            )}
          </Box>

          {/* Reference Image Popup */}
          <Dialog
            open={showReferencePopup && !isLoading}
            onClose={handleCloseReferencePopup}
            // maxWidth="false"
            PaperComponent={PaperComponent}
            hideBackdrop={cameraWithReference}
            PaperProps={{
             sx: {
      width: 400,
      minWidth: 250,
      maxWidth: 800,
      minHeight: 250,
      maxHeight: '80vh',
      resize: 'both',        // ✅ enable resizing
      overflow: 'auto',      // ✅ allow scrolling if content overflows
      position: cameraWithReference ? 'fixed' : 'relative',
      top: cameraWithReference ? '20px' : 'auto',
      right: cameraWithReference ? '20px' : 'auto',
      m: cameraWithReference ? 0 : 'auto',
      zIndex: cameraWithReference ? 1400 : 1300,
      backgroundColor: 'background.paper'
              }
            }}
            sx={{
              '& .MuiBackdrop-root': {
                backgroundColor: cameraWithReference ? 'transparent' : 'rgba(0, 0, 0, 0.5)'
              }
            }}
          >
            <DialogTitle 
              id="draggable-dialog-title"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'move',
                bgcolor: alpha(theme.palette.info.main, 0.1),
                borderBottom: `1px solid ${theme.palette.divider}`
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DragIndicatorIcon color="action" fontSize="small" />
                <VisibilityIcon color="info" fontSize="small" />
                <Typography variant="subtitle1">
                  Baseline Reference
                </Typography>
                <Chip
                  size="small"
                  label="Draggable"
                  color="info"
                  variant="outlined"
                />
              </Box>
              <IconButton onClick={handleCloseReferencePopup} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" fontSize="0.875rem">
                  <strong>Baseline Reference:</strong> "{baselineContext?.baselineAnnotation.label}"
                  {cameraWithReference && (
                    <><br/><strong>Tip:</strong> Drag this window to reposition while using the camera.</>
                  )}
                </Typography>
              </Alert>
              {baselineContext?.baselineAnnotation?.linkedMicroUrl && (
                <Box
                  sx={{
                    width: '100%',
                    height: cameraWithReference ? 250 : 300,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                    '& img': {
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }
                  }}
                >
                  <img
                    src={`${apiBaseUrl}${baselineContext.baselineAnnotation.linkedMicroUrl}`}
                    alt={`Baseline dermatoscopic view of ${baselineContext.baselineAnnotation.label}`}
                  />
                </Box>
              )}
            </DialogContent>
            {!cameraWithReference && (
              <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button
                  onClick={handleCloseReferencePopup}
                  variant="outlined"
                  size="small"
                  startIcon={<CloseIcon />}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProceedWithCamera}
                  variant="contained"
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                >
                  Open Camera with Reference
                </Button>
              </DialogActions>
            )}
            {cameraWithReference && (
              <DialogActions sx={{ p: 2, pt: 0 }}>
                <Button
                  onClick={handleCloseReferencePopup}
                  variant="contained"
                  size="small"
                  color="error"
                  startIcon={<CloseIcon />}
                  fullWidth
                >
                  Close Reference & Camera
                </Button>
              </DialogActions>
            )}
          </Dialog>

          {!isLoading && isBaselineMode && (
            <Alert 
              severity={baselineContext?.followupAnnotation ? "success" : "info"} 
              icon={<TimelineIcon />}
            >
              <Typography variant="body2">
                {baselineContext?.followupAnnotation ? (
                  <>
                    <strong>Follow-up Complete:</strong> New dermatoscopic image has been successfully 
                    added for baseline annotation "{baselineContext.baselineAnnotation.label}". 
                    This creates a new follow-up record linked to the baseline for comparison tracking.
                    You can switch to the Baseline Comparison tab to see the full comparison view.
                  </>
                ) : (
                  <>
                    <strong>Follow-up Mode Active:</strong> New dermatoscopic images will be added as follow-up documentation 
                    for baseline annotation "{baselineContext?.baselineAnnotation.label}". This creates a new follow-up 
                    image linked to the baseline for comparison tracking. Use the Baseline Comparison tab for full analysis.
                  </>
                )}
              </Typography>
            </Alert>
          )}

          {(uploadingMicroImage || isLoading) && (
            <Alert severity="info" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              {isLoading ? "Loading..." : isBaselineMode ? "Uploading follow-up dermatoscopic image..." : "Uploading dermatoscopic image..."}
            </Alert>
          )}

          {/* Annotation details */}
          <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
            {isLoading ? (
              <>
                <Skeleton variant="text" width={150} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="90%" />
              </>
            ) : (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Annotation Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Label:</strong> {displayAnnotation.label}<br />
                  <strong>ID:</strong> {displayAnnotation.id}<br />
                  <strong>Position:</strong> ({Math.round(displayAnnotation.x * 100)}%, {Math.round(displayAnnotation.y * 100)}%)<br />
                  <strong>Size:</strong> {Math.round(displayAnnotation.width * 100)}% × {Math.round(displayAnnotation.height * 100)}%<br />
                  {displayAnnotation.confidence && (
                    <>
                      <strong>Confidence:</strong> {Math.round(displayAnnotation.confidence * 100)}%<br />
                    </>
                  )}
                  {isBaselineMode && baselineContext && (
                    <>
                      <strong>Baseline Session:</strong> {baselineContext.baselineSessionId}<br />
                      <strong>Follow-up Session:</strong> {baselineContext.followupSessionId}
                    </>
                  )}
                </Typography>
              </>
            )}
          </Box>

          {/* Quick Actions */}
          {displayAnnotation.id && (
            <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
              {isLoading ? (
                <>
                  <Skeleton variant="text" width={120} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" width={200} height={32} />
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Analysis Tools
                  </Typography>
                  <Button
                    component={Link}
                    to={`/abcde/${displayAnnotation.id}`}
                    variant="outlined"
                    startIcon={<AssessmentIcon />}
                    size="small"
                    sx={{ mr: 1 }}
                    disabled={isLoading}
                  >
                    Perform ABCDE Assessment
                  </Button>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DermatoscopicPanel;
